import { useState } from 'react';
import { Plus, Pencil, Trash2, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAdminRoles, useDeleteRole } from '@/features/admin/useAdmin';
import { RoleFormDialog } from '@/components/dialogs/admin/RoleFormDialog';
import { ConfirmDialog } from '@/components/dialogs/admin/ConfirmDialog';
import type { AdminRole } from '@/features/admin/types';

export function RolesTab() {
  const rolesQ = useAdminRoles();
  const del = useDeleteRole();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminRole | null>(null);
  const [deleting, setDeleting] = useState<AdminRole | null>(null);

  const roles = rolesQ.data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-lg" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
          Roles y permisos
        </h2>
        <span className="font-mono tabular text-xs" style={{ color: 'var(--text-muted)' }}>
          {roles.length}
        </span>
        <Button variant="primary" size="sm" className="ml-auto" onClick={() => setCreating(true)}>
          <Plus size={14} strokeWidth={2} /> Nuevo rol
        </Button>
      </div>

      {rolesQ.isLoading && (
        <div className="flex h-32 items-center justify-center text-xs" style={{ color: 'var(--text-muted)' }}>
          <Loader2 size={14} className="mr-2 animate-spin" /> Cargando roles...
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {roles.map((r) => (
          <article
            key={r.id}
            className="rounded-[var(--radius-md)] border p-4"
            style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
          >
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="font-mono tabular text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  {r.slug}
                </div>
                <div className="text-sm" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                  {r.label}
                </div>
              </div>
              {r.is_system && (
                <span
                  className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] border px-2 py-0.5 text-[10px]"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
                  title="Rol del sistema (no se puede eliminar)"
                >
                  <Lock size={10} strokeWidth={1.8} /> sistema
                </span>
              )}
            </div>
            {r.description && (
              <p className="mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {r.description}
              </p>
            )}
            <div className="mb-3 flex flex-wrap gap-1">
              {r.permissions.length === 0 && (
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Sin permisos.
                </span>
              )}
              {r.permissions.map((p) => (
                <span
                  key={p}
                  className="rounded-[var(--radius-sm)] border px-1.5 py-0.5 font-mono tabular text-[10px]"
                  style={{
                    borderColor: 'rgba(245,158,11,0.25)',
                    background: 'rgba(245,158,11,0.06)',
                    color: 'var(--accent-soft)',
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                <Pencil size={12} strokeWidth={1.8} /> Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={r.is_system}
                title={r.is_system ? 'Rol del sistema, no se elimina' : 'Eliminar'}
                onClick={() => setDeleting(r)}
              >
                <Trash2 size={12} strokeWidth={1.8} style={{ color: r.is_system ? 'var(--text-disabled)' : 'var(--danger)' }} />
              </Button>
            </div>
          </article>
        ))}
      </div>

      <RoleFormDialog open={creating} onOpenChange={setCreating} />
      <RoleFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} role={editing} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Eliminar el rol "${deleting?.slug ?? ''}"?`}
        description="Solo puedes eliminar roles sin usuarios asignados."
        confirmText={del.isPending ? 'Eliminando...' : 'Eliminar'}
        loading={del.isPending}
        onConfirm={async () => {
          if (deleting) await del.mutateAsync(deleting.id);
        }}
      />
    </div>
  );
}
